import React from "react";
import { Link } from "react-router-dom";
import { LegalHeader, LegalFooter } from "./Terms";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-mb-cream text-neutral-900">
      <LegalHeader />
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16 font-body">
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-neutral-500 mb-10">Last updated: February 14, 2026</p>

        <Section title="1. What we collect">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account:</strong> email, hashed password, optional display name.</li>
            <li><strong>Billing:</strong> if you subscribe, Stripe processes and stores your card. We
              store only the Stripe customer/subscription IDs and last-4 card digits Stripe returns
              on receipts &mdash; never full card numbers, CVCs, or bank details.</li>
            <li><strong>Discord:</strong> if you link your Discord account, we store your Discord user ID
              and username so the bot can grant/revoke the Subscriber role.</li>
            <li><strong>Usage:</strong> standard server logs (IP, user-agent, request path) retained for
              30 days for security &amp; abuse detection.</li>
          </ul>
        </Section>

        <Section title="2. What we don&rsquo;t do">
          <ul className="list-disc pl-6 space-y-2">
            <li>We do not sell your personal information to anyone. Full stop.</li>
            <li>We do not use your data to train third-party AI models.</li>
            <li>We do not share your email with advertisers or affiliate networks.</li>
          </ul>
        </Section>

        <Section title="3. Third parties we share data with">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe</strong> &mdash; payment processing. Their privacy policy: <a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></li>
            <li><strong>Discord</strong> &mdash; only if you explicitly link your account. Their policy: <a className="underline" href="https://discord.com/privacy" target="_blank" rel="noreferrer">discord.com/privacy</a></li>
            <li><strong>MongoDB Atlas / hosting provider</strong> &mdash; encrypted database storage.</li>
            <li><strong>LLM provider (Anthropic Claude)</strong> &mdash; anonymized market data used to generate
              sentiment briefings. Your personal data is never sent to LLMs.</li>
          </ul>
        </Section>

        <Section title="4. Cookies">
          We use a single first-party session cookie to keep you logged in. We do not use third-party
          advertising cookies or trackers. No consent banner is required outside the EU.
        </Section>

        <Section title="5. Data retention">
          Account data is kept as long as your account is active. If you delete your account, we
          delete your profile within 30 days. Billing records are retained for 7 years to comply with
          U.S. tax law. Logs are auto-purged after 30 days.
        </Section>

        <Section title="6. Your rights">
          You may request a copy of the data we hold about you, correct inaccuracies, or delete
          your account entirely by emailing <a className="underline text-mb-emerald" href="mailto:hello@moonbucks.io">hello@moonbucks.io</a>.
          We respond within 30 days.
        </Section>

        <Section title="7. Security">
          Passwords are hashed with bcrypt. All traffic is served over TLS 1.3. Database access is
          restricted by network policy and audited. We&rsquo;ve never had a breach; if we ever do, we&rsquo;ll
          notify affected users within 72 hours as required by GDPR.
        </Section>

        <Section title="8. Children">
          Moonbucks is intended for users 18 or older. If we learn we&rsquo;ve collected data from a minor,
          we delete it immediately.
        </Section>

        <Section title="9. Changes">
          If we make material changes to this policy, we&rsquo;ll email you and update the &ldquo;Last updated&rdquo;
          date at the top.
        </Section>

        <Section title="10. Contact">
          <a className="underline text-mb-emerald" href="mailto:hello@moonbucks.io">hello@moonbucks.io</a>
        </Section>
      </article>
      <LegalFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display text-xl font-semibold mb-3">{title}</h2>
      <div className="text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}
